import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const reducedMotion = useReducedMotion();
  const lastScrollY = useRef(0);
  // Intelligent sticky behavior: hide on scroll down, reveal on scroll up.
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      // Always reveal when the menu is open or near the top of the page.
      if (mobileMenuOpen || y < 80) {
        setHidden(false);
      } else if (y > lastScrollY.current + 6) {
        setHidden(true);
      } else if (y < lastScrollY.current - 6) {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);
  // Reveal the bar whenever the mobile menu opens.
  useEffect(() => {
    if (mobileMenuOpen) setHidden(false);
  }, [mobileMenuOpen]);
  // Footer protection: hide the sticky bar while the footer is in view so it
  // never overlaps footer links or social icons.
  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !mobileMenuOpen) setHidden(true);
    }, {
      threshold: 0.01
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, [mobileMenuOpen]);
  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  const navLinks = t('nav.links') as string[];
  return <motion.header initial={{
    y: -100
  }} animate={{
    y: hidden ? -120 : 0
  }} transition={reducedMotion ? {
    duration: 0
  } : {
    duration: 0.24,
    ease: [0.16, 1, 0.3, 1]
  }} className={`fixed top-0 left-0 right-0 z-40 transition-[padding] duration-300 ${scrolled ? 'py-2.5 md:py-4' : 'py-3 md:py-6'}`} data-id="element-201">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-202">
        <div className={`glass-panel rounded-full pl-4 pr-3 sm:px-6 flex items-center justify-between transition-colors duration-300 h-[60px] md:h-auto md:py-3 ${scrolled ? 'bg-surface/80' : 'bg-surface/40'}`} data-id="element-203">
          {/* Brand lockup — raw transparent PNG, no wrapper/background/effects */}
          <a href="#" aria-label="Blinkblis" className="inline-flex items-center gap-2.5 sm:gap-3 bg-transparent p-0 border-0 shadow-none cursor-pointer group shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-204">
            <img src="https://cdn.magicpatterns.com/uploads/w8wf8HrNFmFRNJWrke4AoT/blinkblis-navbar-mark-transparent-512.png" alt="Blinkblis" className="block h-11 w-11 md:h-12 md:w-12 shrink-0 object-contain bg-transparent p-0 m-0 border-0 shadow-none rounded-none" style={{
            background: 'transparent',
            backgroundColor: 'transparent',
            padding: 0,
            border: 0,
            boxShadow: 'none',
            filter: 'none',
            borderRadius: 0
          }} data-id="element-205" />
            <span className="block text-white text-[22px] sm:text-[22px] font-semibold tracking-[-0.02em] leading-none group-hover:text-white/90 transition-colors" data-id="element-206">
              Blinkblis
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7 mx-6" data-id="element-207">
            {navLinks.map(link => <a key={link} href={`#${link.toLowerCase()}`} className="text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-208">
                {link}
              </a>)}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-4 shrink-0" data-id="element-209">
            <div className="flex items-center gap-1 text-xs font-medium text-white/50 bg-white/5 p-1 rounded-full border border-white/10" data-id="element-210">
              <button onClick={() => setLanguage('en')} className={`px-2.5 py-1 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'en' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-211">
                EN
              </button>
              <button onClick={() => setLanguage('es')} className={`px-2.5 py-1 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'es' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-212">
                ES
              </button>
            </div>
            <button className="bg-white text-background px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/90 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(255,255,255,0.1)] active:scale-[0.98] transition-all duration-300 whitespace-nowrap shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-213">
              {t('nav.startExploring')}
            </button>
          </div>

          {/* Mobile Toggle */}
          <button className="md:hidden text-white/80 hover:text-white w-11 h-11 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 active:bg-white/5 transition-colors" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-214">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-215" /> : <Menu className="w-6 h-6" data-id="element-216" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-217">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={reducedMotion ? {
        duration: 0
      } : {
        duration: 0.22
      }} className="md:hidden absolute top-full left-0 right-0 mt-2 px-4 max-h-[calc(100dvh-90px)] overflow-y-auto" data-id="element-218">
            <div className="relative rounded-3xl p-6 flex flex-col gap-6 bg-background/85 backdrop-blur-[14px] border border-white/10 overflow-hidden" data-id="element-219">
              {/* Very soft orange ambient light only — no bright fog/bands */}
              <div className="absolute -top-16 -right-10 w-48 h-48 bg-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-220" />

              <nav className="relative flex flex-col gap-7" data-id="element-221">
                {navLinks.map(link => <a key={link} href={`#${link.toLowerCase()}`} onClick={() => setMobileMenuOpen(false)} className="text-[23px] font-display font-medium text-white/85 hover:text-white transition-colors leading-none min-h-[44px] flex items-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-222">
                    {link}
                  </a>)}
              </nav>

              <div className="relative h-px bg-white/10" data-id="element-223" />

              <div className="relative flex flex-col gap-5" data-id="element-224">
                <div className="flex items-center gap-1 text-sm font-medium text-white/50 bg-white/5 p-1 rounded-full border border-white/10 w-fit" data-id="element-225">
                  <button onClick={() => setLanguage('en')} className={`px-5 py-2 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'en' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-226">
                    EN
                  </button>
                  <button onClick={() => setLanguage('es')} className={`px-5 py-2 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'es' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-227">
                    ES
                  </button>
                </div>
                <button className="w-full bg-white text-background px-5 h-[52px] rounded-full text-[16px] font-medium active:scale-[0.98] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-228">
                  {t('nav.startExploring')}
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}